/**
 * Templates with variable content, and the merge that fills them.
 *
 * The proposal template is a real document with {{tokens}} in it — cover title,
 * client name, investment, validity. Filling those by hand is how a proposal
 * goes out addressed to the previous client, and leaving one unfilled is how it
 * goes out reading "Dear {{contact_name}}".
 *
 * So the one rule everything here serves: **a document is never sent with a
 * token still in it.** The merge reports what is missing rather than quietly
 * substituting a blank, because a blank looks finished and a missing name does
 * not announce itself in a PDF.
 */

// {{token}} or {{ token }}. Deliberately narrow: word characters and dots, so
// prose containing braces cannot accidentally become a variable.
const TOKEN = /\{\{\s*([a-zA-Z][\w.]*)\s*\}\}/g

/** Every token the template uses, in the order it first appears. */
export function tokensIn(body) {
  const seen = []
  const text = String(body || '')
  let m
  TOKEN.lastIndex = 0
  while ((m = TOKEN.exec(text)) !== null) {
    if (!seen.includes(m[1])) seen.push(m[1])
  }
  return seen
}

const isEmpty = (v) =>
  v === null || v === undefined || (typeof v === 'string' && v.trim() === '')

/**
 * What a template declares about one of its variables.
 *
 * `required` defaults to true: a variable somebody put in the document is
 * presumed to matter, and the safe direction for a document that goes to a
 * client is to refuse rather than to render.
 */
export function declaredVariable(declaration = {}, key) {
  return {
    key,
    label: declaration.label || key.replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    help: declaration.help || null,
    required: declaration.required !== false,
    // A default is for something with a house answer — validity days, the
    // studio's own name — not for a client-specific fact.
    default: declaration.default ?? null,
    source: declaration.source || null,
  }
}

/**
 * The variables a template needs, merged with what it declares about them.
 *
 * A token nobody declared still appears, because the document uses it and
 * pretending otherwise would leave it unfillable. A declaration for a token the
 * body no longer contains is dropped — it is a leftover, not a requirement.
 */
export function templateVariables(template) {
  const declarations = template?.variables || {}
  return tokensIn(template?.content).map(key => declaredVariable(declarations[key], key))
}

/**
 * Fill a template.
 *
 * Returns the filled text alongside what could not be filled. The caller
 * decides what to do about it — but `ok` is false whenever a required variable
 * is missing, and every screen that sends is expected to honour that.
 */
export function mergeTemplate(template, values = {}) {
  const body = String(template?.content || '')
  const vars = templateVariables(template)
  const byKey = Object.fromEntries(vars.map(v => [v.key, v]))

  const missing = []
  const filled = {}

  for (const v of vars) {
    const given = values[v.key]
    const value = isEmpty(given) ? v.default : given
    if (isEmpty(value)) {
      if (v.required) missing.push(v)
      filled[v.key] = null
    } else {
      filled[v.key] = String(value)
    }
  }

  TOKEN.lastIndex = 0
  const text = body.replace(TOKEN, (whole, key) => {
    const value = filled[key]
    // An unfilled token is left visible on purpose. Replacing it with an empty
    // string produces a document that looks complete and says something false —
    // "located at , " — which is worse than one that obviously is not ready.
    return value === null || value === undefined ? whole : value
  })

  // Values handed in that the document has no place for. Not an error, but
  // worth surfacing: it usually means a template was edited and a field left
  // behind, or a field was renamed in one place only.
  const unknown = Object.keys(values).filter(k => !byKey[k] && !isEmpty(values[k]))

  return { ok: missing.length === 0, text, missing, unknown, variables: vars }
}

/** What to say when a merge is not ready, naming the fields rather than counting them. */
export function describeMissing(missing = []) {
  if (!missing.length) return null
  const names = missing.map(m => m.label)
  if (names.length === 1) return `${names[0]} is still empty.`
  if (names.length === 2) return `${names[0]} and ${names[1]} are still empty.`
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} are still empty.`
}

/**
 * The values a template can fill on its own, from what the portal already knows.
 *
 * Only facts, never guesses. A template asking for `cover_title` gets nothing
 * from here: that is a decision somebody makes per proposal, and inventing one
 * is how every proposal ends up with the same headline.
 */
export function autofill({ client, project, proposal, now = new Date() } = {}) {
  const out = {}
  const put = (key, value) => { if (!isEmpty(value)) out[key] = String(value) }

  put('client_name', client?.company || client?.name)
  put('contact_name', client?.name)
  // "Jonathan, here is the scope we discussed" — a page opens on a first name.
  put('contact_first_name', String(client?.name || '').trim().split(/\s+/)[0])
  put('client_email', client?.email)
  put('client_phone', client?.phone)
  put('client_street', client?.address)
  put('client_locality', [client?.city, client?.state, client?.zip].filter(Boolean).join(', '))
  put('project_title', project?.title)
  put('proposal_number', proposal?.proposal_number)

  put('proposal_date', now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }))

  const validity = Number(proposal?.validity_days) || 30
  put('validity_days', String(validity))
  const through = new Date(now.getFullYear(), now.getMonth(), now.getDate() + validity)
  put('valid_through', through.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }))

  const total = Number(proposal?.total)
  if (Number.isFinite(total) && total > 0) {
    put('total_investment', `$${Math.round(total).toLocaleString('en-US')}`)
    // The short name the page library writes; the same figure.
    put('total', `$${Math.round(total).toLocaleString('en-US')}`)
  }

  return out
}

/** A template ready to be duplicated, without carrying one client's answers. */
export function duplicateTemplate(template, { name } = {}) {
  if (!template) return { error: 'Nothing to duplicate.' }
  return {
    error: null,
    template: {
      name: name || `${template.name} copy`,
      category: template.category || null,
      description: template.description || null,
      content: template.content || '',
      variables: template.variables || {},
      kind: template.kind || 'proposal',
      is_active: false,
    },
  }
}
